import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Target, TrendingUp, BookOpen, Shield, Calendar, ArrowLeft, Zap } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { ISOProgressionPath } from '../components/training/ISOProgressionPath';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';
import { standards } from '../data/standards/standardsData';

export const ISO50001LeadAuditor = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "ISO 50001 Lead Auditor Course | Energy Management System Certification",
    "description": "Professional ISO 50001 Lead Auditor Course for energy management auditors. Globally recognized EnMS lead auditor certification covering energy performance auditing, EnPI verification, and sustainability compliance.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "sameAs": "https://www.isde.academy"
    },
    "coursePrerequisites": "Energy management or internal auditing experience recommended",
    "educationalLevel": "Advanced",
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": ["In-person", "Online"],
      "duration": "5 days"
    }
  };

  const courseCompetencies = [
    'Lead EnMS audit teams across diverse industrial and commercial organizations',
    'Evaluate energy performance, significant energy uses (SEUs), and EnPIs',
    'Verify regulatory compliance and energy-related legal obligations',
    'Drive continual energy improvement and support ISO 50001 certification'
  ];

  const targetAudience = [
    'Energy Managers and Energy Officers',
    'Environmental and Sustainability Professionals',
    'ESG and Corporate Responsibility Managers',
    'Facility Managers and Building Energy Specialists',
    'Internal and External Management System Auditors',
    'Management Representatives for EnMS',
    'Graduates aspiring to become energy management lead auditors'
  ];

  const professionalBenefits = [
    {
      title: 'Internationally recognized energy management auditor qualification',
      icon: Award
    },
    {
      title: 'Career advancement in energy auditing, sustainability, and ESG roles',
      icon: TrendingUp
    },
    {
      title: 'Enhanced professional credibility as an ISO 50001 and EnMS expert',
      icon: Shield
    }
  ];

  const organizationalBenefits = [
    {
      title: 'Ability to lead internal, supplier, and certification EnMS audits',
      icon: Users
    },
    {
      title: 'Improved energy performance measurement and operational efficiency',
      icon: Target
    },
    {
      title: 'Reduced energy costs, carbon emissions, and compliance risks',
      icon: CheckCircle
    }
  ];

  const businessBenefits = [
    {
      title: 'Supports ESG reporting, sustainability goals, and net zero commitments',
      icon: Zap
    },
    {
      title: 'Enhances stakeholder and investor confidence in energy governance',
      icon: Award
    },
    {
      title: 'Meets regulatory energy efficiency and carbon reduction requirements',
      icon: CheckCircle
    }
  ];

  const careerOpportunities = [
    'ISO 50001 Lead Auditor',
    'Energy Management Consultant',
    'ESG & Sustainability Auditor',
    'Energy Efficiency Specialist',
    'Third-party Certification Auditor',
    'Corporate Energy Manager'
  ];

  const industries = [
    'Manufacturing & Process Industry',
    'Energy Generation & Utilities',
    'Oil & Gas',
    'Healthcare & Hospitals',
    'Data Centers & IT',
    'Commercial Buildings',
    'Government & Public Sector'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="ISO 50001 Lead Auditor Course | Energy Management System Certification | ISDE Academy"
        description="Earn ISO 50001 Lead Auditor certification with ISDE Academy. 5-day expert-led EnMS lead auditor course covering energy performance auditing, EnPI verification, SEU assessment, and sustainability compliance. Advance your energy management career."
        keywords="ISO 50001 lead auditor, energy management lead auditor certification, EnMS lead auditor course, ISO 50001 auditor training India, energy efficiency auditor, sustainability auditor certification, ISO 50001 certification, ESG auditor training"
        canonical="/ISO-50001-Lead-Auditor-Course"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Training', path: '/training' },
          { name: 'ISO Training Services', path: '/training/iso-training-services' },
          { name: 'ISO 50001 Lead Auditor', path: '/ISO-50001-Lead-Auditor-Course' }
        ]}
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-green-700 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-sm mb-6 font-semibold">
              <Zap className="h-5 w-5" />
              ENERGY MANAGEMENT SYSTEMS
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              ISO 50001 Lead Auditor Course
            </h1>
            <p className="text-xl md:text-2xl text-emerald-100 mb-8 max-w-4xl mx-auto leading-relaxed">
              Become a globally recognized ISO 50001 Lead Auditor. Master energy management system audits, drive energy efficiency improvements, and contribute to a more sustainable future.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-lg">
              <div className="flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                <span>5 Days Training</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-6 w-6" />
                <span>Professional Certificate</span>
              </div>
              <div className="flex items-center gap-2">
                <BookOpen className="h-6 w-6" />
                <span>Global Recognition</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Back Button */}
      <section className="bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            onClick={() => navigate('/training/iso-training-services')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to ISO Training Services
          </Button>
        </div>
      </section>

      {/* ISO Progression Path */}
      <ISOProgressionPath standard={standards['iso-50001']} currentLevel="leadAuditor" />

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The ISO 50001 Lead Auditor Course is a globally recognized professional training programme for energy management professionals seeking to develop advanced competence in auditing Energy Management Systems (EnMS) in accordance with ISO 50001:2018. As organizations face increasing pressure to reduce energy costs, meet carbon reduction targets, and demonstrate ESG accountability, the demand for qualified ISO 50001 lead auditors has grown significantly across manufacturing, commercial, and public sector organizations worldwide.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              This 5-day intensive course equips participants with in-depth knowledge of ISO 50001 requirements, advanced energy auditing methodologies, EnPI verification techniques, and internationally recognized certification credentials — enabling them to lead EnMS audits that deliver measurable energy performance improvements and support organizational sustainability goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is ISO 50001 Lead Auditor Course Section */}
      <section className="py-16 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Is the ISO 50001 Lead Auditor Course?
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              The ISO 50001 Lead Auditor Course provides comprehensive training on conducting first-party, second-party, and third-party audits of Energy Management Systems based on ISO 50001:2018. Following ISO 19011 guidelines, participants learn to plan energy performance audits, evaluate significant energy uses (SEUs), verify energy performance indicators (EnPIs), assess energy baselines, and lead audit teams to drive genuine energy efficiency improvements.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="h-full border-2 border-emerald-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Certified participants gain competence to:
                  </h3>
                  <div className="space-y-3">
                    {courseCompetencies.map((competency, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{competency}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-center"
            >
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 shadow-2xl mb-4">
                  <Zap className="h-16 w-16 text-white" />
                </div>
                <p className="text-lg font-semibold text-gray-800">
                  Certified Energy Management<br />Lead Auditor
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Should Attend Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Who Should Attend the ISO 50001 Lead Auditor Course?
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              This course is ideal for energy management and sustainability professionals seeking advanced auditor credentials, including:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {targetAudience.map((role, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Card className="h-full border-l-4 border-emerald-500 hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800">{role}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Benefits of ISO 50001 Lead Auditor Course
            </h3>
          </motion.div>

          {/* Professional Benefits */}
          <div className="mb-12">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">Professional Benefits</h4>
            <div className="grid md:grid-cols-3 gap-6">
              {professionalBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full bg-white hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-emerald-100 to-green-200 mb-4">
                        <benefit.icon className="h-8 w-8 text-emerald-700" />
                      </div>
                      <p className="text-gray-800 font-medium">{benefit.title}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Organizational Benefits */}
          <div className="mb-12">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">Organizational Benefits</h4>
            <div className="grid md:grid-cols-3 gap-6">
              {organizationalBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full bg-white hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-emerald-100 to-green-200 mb-4">
                        <benefit.icon className="h-8 w-8 text-emerald-700" />
                      </div>
                      <p className="text-gray-800 font-medium">{benefit.title}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Business & Market Benefits */}
          <div>
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">Business & Market Benefits</h4>
            <div className="grid md:grid-cols-3 gap-6">
              {businessBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full bg-white hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-emerald-100 to-green-200 mb-4">
                        <benefit.icon className="h-8 w-8 text-emerald-700" />
                      </div>
                      <p className="text-gray-800 font-medium">{benefit.title}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Opportunities Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Career Opportunities After ISO 50001 Lead Auditor Course
            </h4>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Earning ISO 50001 Lead Auditor certification opens high-demand career pathways in the growing energy management and sustainability sector:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            {careerOpportunities.map((career, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Card className="h-full bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200 hover:shadow-lg transition-all">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center">
                      <Award className="h-5 w-5 text-white" />
                    </div>
                    <span className="font-semibold text-gray-900">{career}</span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Industries Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-emerald-600 to-green-700 rounded-2xl p-8 text-white"
          >
            <h5 className="text-2xl font-bold mb-4 text-center">
              Industries Hiring ISO 50001 Lead Auditors
            </h5>
            <div className="flex flex-wrap justify-center gap-4">
              {industries.map((industry, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full font-medium"
                >
                  {industry}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug="iso-50001" />

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Become an ISO 50001 Lead Auditor?
            </h2>
            <p className="text-xl text-emerald-100 mb-8">
              Join energy management professionals worldwide who have advanced their careers with our comprehensive ISO 50001 Lead Auditor certification
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/contact')}
                className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-8 py-6 text-lg"
              >
                Enroll Now
              </Button>
              <Button
                onClick={() => navigate('/training-calendar')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-emerald-700 font-semibold px-8 py-6 text-lg"
              >
                View Schedule
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
